import { useEffect, useMemo, useRef, useState } from 'react';

export type HashParams = { [key: string]: string | undefined };

export const hashToParams = (hash: string) => {
  const trimmedHash = hash ? hash.slice(1) : '';
  const keyVals = trimmedHash.split(',');
  const ret: HashParams = {};
  keyVals.forEach((kvp) => {
    if (!kvp) return;
    const [key, val] = kvp.split('=');
    ret[key] = val || '';
  });
  return ret;
};

export const paramsToHash = (params: HashParams) => {
  const keys = Object.keys(params).filter((key) => params[key] != null);
  if (keys.length === 0) return '';
  return '#' + keys.map((key) => `${key}=${params[key]}`).join(',');
};

export const useHashParams = () => {
  const [_, setToken] = useState({});
  const hash = useRef(window.location.hash);
  hash.current = window.location.hash;
  const [params, setParams] = useState(() => hashToParams(hash.current));
  // synchronize param object with hash
  useEffect(() => setParams(hashToParams(hash.current)), [
    hash.current,
    setParams,
  ]);
  // synchronize hash with param object
  useEffect(() => {
    window.location.hash = paramsToHash(params);
  }, [params]);
  // listen to hash changes
  useEffect(() => {
    const onHashChange = () => setToken({});
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [setToken]);
  // return method for setting hash by setting params
  const setHashParams = useMemo(
    () => (params: HashParams) => {
      const newParams = { ...hashToParams(window.location.hash), ...params };
      setParams(newParams);
    },
    [setParams]
  );
  return { hashParams: params, setHashParams };
};
